import identity from "lodash/identity";
import { useCallback, useMemo } from "react";

import type { Nullable } from "@/types/utils";
import { extractClosingPhrase } from "@/utils/screener-builder";

// static array of predetermined closing phrases
// to validate against for question validation on review page
export const closingPhrases = [
  "goodbye",
  "have a great day",
  "next steps",
  "it was a pleasure speaking with you today",
  "thank you for your time",
  "i appreciate your insights",
  "that's all the questions I have",
  "i look forward to hearing from you",
  "let's wrap this up",
  "this concludes our interview",
  "i think we've covered everything",
  "adios",
  "gracias por tus respuestas reflexivas",
  "tengas un excelente día",
  "esta conclusión nuestra entrevista",
  "ahora hemos llegado al final de nuestra entrevista",
  "que tenga un buen dia",
  "gracias por su tiempo",
  "gracias por tu tiempo y reflexivas respuestas",
  "ha sido un placer hablar con usted",
  "eso es todo lo que necesitaba saber",
  "espero su respuesta",
  "con esto concluimos",
  "no tengo mas preguntas",
  "agradezco su colaboracion",
  "tengas un execelente dia",
  "concluye nuestra entrevista",
  "mesi pou patisipasyon ou",
  "sa fini entevyou a",
  "hemos concluido la entrevista",
  "sa se yon bon fason pou fini entevyou a",
  "se yon plezi pou nou te pale avek ou",
  "sa fini entevyou a",
  "epi pral kontakte ou",
  "adyeu",
  "orevwa",
  "pase yon bel jou",
  "pwochen etap yo",
  "se te yon plezi pale avek ou jodi a",
  "mesi pou tan ou",
  "sa se tout kesyon mwen genyen",
  "mwen tann nouvel ou avek enpasyans",
  "ann fini sa",
  "entevyou a fini la",
  "mwen kwe nou kouvri tout bagay",
  "au revoir",
  "passez une excellente journée",
  "prochaines étapes",
  "ce fut un plaisir de parler avec vous aujourd'hui",
  "merci pour votre temps",
  "j'apprécie vos idées",
  "c'est toutes les questions que j'ai",
  "j'ai hâte d'avoir de vos nouvelles",
  "concluons cela",
  "cela conclut notre entretien",
  "je pense que nous avons tout couvert",
  "sa fini entèvyou nou an",
  "esta entrevista concluye aquí",
  "esta es la conclusión de nuestra entrevista",
  "esta entrevista ha concluido",
];

interface UseContainsClosingPhraseOptions {
  formatFn?: (closingPhrase: string) => string;
  minLength?: number;
}

interface UseContainsClosingPhraseReturn {
  closingPhrase?: string;
  errorMessage: Nullable<string>;
  isValid: boolean;
}

export const containsClosingPhrase = (
  value = "",
  { formatFn = identity, minLength = 5 }: UseContainsClosingPhraseOptions = {},
) => {
  const closingPhrase =
    value.trim().length >= minLength ? extractClosingPhrase(value) : undefined;

  return closingPhrase
    ? {
        closingPhrase,
        errorMessage: formatFn(closingPhrase),
        isValid: false,
      }
    : { closingPhrase, errorMessage: null, isValid: true };
};

export const useContainsClosingPhrase = (
  value = "",
  options: UseContainsClosingPhraseOptions = {},
): UseContainsClosingPhraseReturn => {
  return useMemo(() => containsClosingPhrase(value, options), [options, value]);
};

export const useGetCharCountValidation = (maxChars: number) =>
  useCallback(
    (value: string) => {
      const valueLength = value.length;

      return valueLength > maxChars
        ? `Max ${maxChars} characters (${valueLength}/${maxChars})`
        : null;
    },
    [maxChars],
  );
